import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Pencil, Trash2, Plus, X } from 'lucide-react';
import RichTextEditor from '@/components/RichTextEditor';
import { 
  getPublishedBlogs, 
  createBlogPost, 
  updateBlogPost, 
  deleteBlogPost 
} from '@/services/cms';

const BlogManager = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingBlog, setEditingBlog] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    excerpt: '',
    author: '',
    category: '',
    featuredImage: '',
    featuredImageAlt: '',
    published: true,
    seoMetadata: {
      metaTitle: '',
      metaDescription: '',
      keywords: []
    }
  });

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const data = await getPublishedBlogs();
      setBlogs(data);
    } catch (error) {
      console.error('Error fetching blogs:', error);
      toast.error('Failed to load blogs');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    
    try {
      // Convert keywords string to array if needed
      const submitData = {
        ...formData,
        seoMetadata: {
          ...formData.seoMetadata,
          keywords: Array.isArray(formData.seoMetadata.keywords) 
            ? formData.seoMetadata.keywords 
            : formData.seoMetadata.keywords.split(',').map(k => k.trim()).filter(k => k)
        }
      };

      if (editingBlog) {
        await updateBlogPost(editingBlog.id, submitData);
        toast.success('Blog updated successfully');
      } else {
        await createBlogPost(submitData);
        toast.success('Blog created successfully');
      }
      fetchBlogs();
      resetForm();
    } catch (error) {
      console.error('Error saving blog:', error);
      toast.error(error.message || 'Failed to save blog');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (blogId) => {
    if (!window.confirm('Are you sure you want to delete this blog?')) return;
    
    try {
      await deleteBlogPost(blogId);
      toast.success('Blog deleted successfully');
      fetchBlogs();
    } catch (error) {
      console.error('Error deleting blog:', error);
      toast.error('Failed to delete blog');
    }
  };

  const handleEdit = (blog) => {
    setEditingBlog(blog);
    setFormData({
      ...blog,
      seoMetadata: {
        metaTitle: blog.seoMetadata?.metaTitle || '',
        metaDescription: blog.seoMetadata?.metaDescription || '',
        keywords: Array.isArray(blog.seoMetadata?.keywords) 
          ? blog.seoMetadata.keywords.join(', ')
          : blog.seoMetadata?.keywords || ''
      }
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      slug: '',
      content: '',
      excerpt: '',
      author: '',
      category: '',
      featuredImage: '',
      featuredImageAlt: '',
      published: true,
      seoMetadata: {
        metaTitle: '',
        metaDescription: '',
        keywords: []
      }
    });
    setEditingBlog(null);
    setShowForm(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold">Blog Management</h2>
          <p className="text-gray-600 mt-1">Create and manage blog posts</p>
        </div>
        <Button 
          onClick={() => setShowForm(!showForm)}
          className="bg-gradient-to-r from-cyan-600 to-turquoise-600"
        >
          {showForm ? (
            <>
              <X className="mr-2 w-4 h-4" />
              Cancel
            </>
          ) : (
            <>
              <Plus className="mr-2 w-4 h-4" />
              Create New Blog
            </>
          )}
        </Button>
      </div>

      {showForm && (
        <Card className="p-6 bg-gradient-to-br from-cyan-50 to-white">
          <h3 className="text-xl font-bold mb-4">
            {editingBlog ? 'Edit Blog Post' : 'Create New Blog Post'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <Input
                placeholder="Title *"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
              <Input
                placeholder="Slug (URL friendly) *"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                required
              />
            </div>

            <div>
              <label className="text-sm font-semibold text-gray-700 mb-2 block">Content (Rich Text Editor) *</label>
              <RichTextEditor
                value={formData.content}
                onChange={(content) => setFormData({ ...formData, content })}
                placeholder="Write your blog content here... Use the toolbar to format text, add headings, lists, colors, etc."
              />
            </div>

            <Textarea
              placeholder="Excerpt *"
              value={formData.excerpt}
              onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
              rows={3}
              required
            />

            <div className="grid md:grid-cols-2 gap-4">
              <Input
                placeholder="Author *"
                value={formData.author}
                onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                required
              />
              <Input
                placeholder="Category *"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                required
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <Input
                placeholder="Featured Image URL *"
                value={formData.featuredImage}
                onChange={(e) => setFormData({ ...formData, featuredImage: e.target.value })}
                required
              />
              <Input
                placeholder="Featured Image Alt Text"
                value={formData.featuredImageAlt}
                onChange={(e) => setFormData({ ...formData, featuredImageAlt: e.target.value })}
              />
            </div>
            
            {/* SEO Fields */}
            <div className="border-t pt-4 mt-4">
              <h4 className="font-semibold mb-3 text-cyan-600">SEO Settings</h4>
              <div className="space-y-4">
                <Input
                  placeholder="Meta Title"
                  value={formData.seoMetadata.metaTitle}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    seoMetadata: { ...formData.seoMetadata, metaTitle: e.target.value }
                  })}
                />
                <Textarea
                  placeholder="Meta Description"
                  value={formData.seoMetadata.metaDescription}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    seoMetadata: { ...formData.seoMetadata, metaDescription: e.target.value }
                  })}
                  rows={2}
                />
                <Input
                  placeholder="Keywords (comma-separated)"
                  value={formData.seoMetadata.keywords}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    seoMetadata: { ...formData.seoMetadata, keywords: e.target.value }
                  })}
                />
              </div>
            </div>

            <div className="flex gap-4 pt-4">
              <Button 
                type="submit" 
                className="flex-1 bg-gradient-to-r from-cyan-600 to-turquoise-600"
                disabled={formLoading}
              >
                {formLoading ? 'Saving...' : (editingBlog ? 'Update Blog' : 'Create Blog')}
              </Button>
              <Button type="button" variant="outline" onClick={resetForm}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Blog List */}
      <div className="space-y-4">
        {blogs.length === 0 ? (
          <Card className="p-8 text-center">
            <p className="text-gray-600">No blog posts yet. Create your first one!</p>
          </Card>
        ) : (
          blogs.map((blog) => (
            <Card key={blog.id} className="p-6 hover:shadow-lg transition-shadow">
              <div className="flex gap-4">
                {blog.featuredImage && (
                  <img 
                    src={blog.featuredImage} 
                    alt={blog.title}
                    className="w-32 h-32 object-cover rounded-lg"
                  />
                )}
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-xl font-bold mb-1">{blog.title}</h3>
                      <p className="text-sm text-gray-500 mb-2">
                        <span className="bg-cyan-100 text-cyan-700 px-2 py-1 rounded text-xs font-semibold">
                          {blog.category}
                        </span>
                        <span className="ml-2">by {blog.author}</span>
                        <span className="ml-2">• {new Date(blog.publishedAt).toLocaleDateString()}</span>
                      </p>
                      <p className="text-gray-600 line-clamp-2">{blog.excerpt}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(blog)}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDelete(blog.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default BlogManager;
